import {
  useEffect,
  useState,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { CSVLink } from "react-csv";
import { useNavigate } from "react-router-dom";
import AuthContext from "../../../auth/AuthContext";
import ReportService from "../../../services/ReportService";
import AlertService from "../../../services/AlertService";
import CourseService from "../../../services/CourseService";
import { Dropdown } from "primereact/dropdown";

const IncidentMisReport = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const isInitialMount = useRef(true);
  const abortControllerRef = useRef(null); 
  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);

  const headers = [
    { label: "Learner Name", key: "userName" },
    { label: "Total Incidents", key: "totalIncidents" },
    { label: "Time Spent", key: "timeSpentDesc" },
    { label: "Completed", key: "completed" },
    { label: "In-Complete", key: "inComplete" },
  ];

  const supportAccess = authContext.getPermissions("report");

  useEffect(() => {
    if (
      supportAccess.filter((f) => f.access.includes(1)).length === 0 ||
      authContext.onGoingValidations
    ) {
      navigate("/");
    }
  }, [supportAccess, navigate, authContext]);

const loadCourses = useCallback(async (signal) => {
    try {
      const data = await CourseService.GetCourses(authContext, signal);

      if (signal?.aborted) return;

      if (data.isSuccess) {
        const options = [{ value: 0, label: "All" }, ...data.data];
        setCourses(options);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return;
    } 
    // finally {
    //   if (!signal?.aborted) setLoading(false); 
    // }
  }, [authContext]);

 const loadResults = useCallback(async (selectedCourseId) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setResults([]);
    setFilteredResults([]);

    try {
      const data = await ReportService.getIncidentMISReport(
        selectedCourseId,
        authContext,
        controller.signal 
      );

      if (controller.signal.aborted) return; 

      if (data.isSuccess) {
        setResults(data.data);
        setFilteredResults(data.data);
        setCurrentPage(0);
      } else {
        AlertService.error(data.errorMessage);
      }
    } catch (err) {
      if (err.name === "AbortError") return;
    } finally {
      if (!controller.signal.aborted) setLoading(false); 
    }
  }, [authContext]);

useEffect(() => {
    const controller = new AbortController(); 
    loadCourses(controller.signal);
    return () => controller.abort(); 
  }, [loadCourses]);


useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    }

    loadResults(courseId);

    return () => {
      abortControllerRef.current?.abort(); 
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


const handleCourseFilter = (value) => {
    setCourseId(value);
    loadResults(value); 
  };

  const paginatedClients = useMemo(() => {
  const startIndex = currentPage * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

    if (loading) {
      return Array.from({ length: rowsPerPage }, (_, i) => ({ id: i }));
    }

    return filteredResults.slice(startIndex, endIndex);
  }, [filteredResults, currentPage, rowsPerPage, loading]);

    const generatePageNumbers = () => {
    const totalPages = Math.ceil(filteredResults.length / rowsPerPage);
    const currentPageNumber = currentPage + 1;
    const pages = [];
    const maxVisiblePages = 3;

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPageNumber > maxVisiblePages + 1) pages.push("...");

      let start = Math.max(2, currentPageNumber - 1);
      let end = Math.min(totalPages - 1, currentPageNumber + 1);

      if (currentPageNumber <= maxVisiblePages) end = 4;
      else if (currentPageNumber > totalPages - maxVisiblePages)
        start = totalPages - 3;

      for (let i = start; i <= end; i++) {
        if (i > 1 && i < totalPages) pages.push(i);
      }

      if (currentPageNumber < totalPages - maxVisiblePages) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const skeletonCell = () => <Skeleton width="100%" height="1.5rem" />;

  return (
    <div className="container-fluid custom-container position-relative mb-5">
      <div className="d-flex flex-column flex-md-row justify-content-end align-items-start align-items-md-center mb-3">
        <div className="d-flex flex-wrap gap-4 pt-3">
            <div className="flex-grow-1"   style={{ minWidth: "220px", maxWidth: "300px" }}>
  <Dropdown
    value={courseId}
    onChange={(e) => handleCourseFilter(Number(e.value))}
    options={courses}
    optionLabel="label"
    optionValue="value"
    placeholder="Select a Course"
    filter
    filterPlaceholder="Search course..."
    disabled={loading}
    className="custom-input-color report-dropdown"
    panelClassName="report-dropdown-panel"
    emptyMessage="No courses found"
    emptyFilterMessage="No matching course found"
  />
</div>
          <div>
            <CSVLink
              data={results}
              headers={headers}
              filename={"Incident-Results.csv"}
              className="btn btn-primary"
            >
              <i className="pi pi-upload me-2"></i>Export
            </CSVLink>
          </div>
        </div>
      </div>

      <DataTable
        value={paginatedClients}
        selection={selectedClients}
        onSelectionChange={(e) => setSelectedClients(e.value)}
        selectionMode="checkbox"
        responsiveLayout="scroll"
        className="custom-table"
        emptyMessage={loading ? null : "No Data Found"}
         pt={{
    root: { style: { cursor: "default" } },
    bodyRow: { style: { cursor: "default" } },
    checkbox: { style: { cursor: "default" } }
  }}
      >
        <Column
          header="S. No."
          body={
            loading
              ? skeletonCell()
              : (_rowData, { rowIndex }) =>
                  currentPage * rowsPerPage + rowIndex + 1
          }
          style={{ width: "80px", textAlign: "center" }}
          bodyClassName="text-center"
          headerClassName="text-center"
        />
        <Column
          field="userName"
          header="Learner Name"
          sortable
          body={(rowData) => (loading ? skeletonCell() : rowData.userName)}
        />
        <Column
          field="totalIncidents"
          header="Total Incidents"
          body={(rowData) =>
            loading ? skeletonCell() : rowData.totalIncidents
          }
        />
        <Column
          field="timeSpentDesc"
          header="Time Spent"
          body={(rowData) => (loading ? skeletonCell() : rowData.timeSpentDesc)}
        />
        <Column
          field="completed"
          header="Completed"
          body={(rowData) => (loading ? skeletonCell() : rowData.completed)}
        />
        <Column
          field="inComplete"
          header="In-Complete"
          body={(rowData) => (loading ? skeletonCell() : rowData.inComplete)}
        />
      </DataTable>

      <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-center mt-3 gap-2 paginator-wrapper">
        <div className="d-flex align-items-center">
          <label className="me-2 fw-bold">Show Rows:</label>
          <select
            className="form-select form-select-sm w-auto py-2 text-secondary custom-input-color"
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(parseInt(e.target.value));
              setCurrentPage(0);
            }}
          >
            {[10, 20, 50].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        <div className="d-flex justify-content-center align-items-center gap-2">
          <button
            className="btn p-0 fs-5 pagination-item"
            style={{ width: "40px", height: "40px" }}
            onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
          >
            &lt;
          </button>

          {generatePageNumbers().map((page, index) =>
            page === "..." ? (
              <span key={index} className="mx-1 text-secondary">
                ...
              </span>
            ) : (
              <button
                key={index}
                className={`btn p-0 pagination-item ${
                  currentPage + 1 === page ? "active-page" : ""
                }`}
                style={{ width: "40px", height: "40px" }}
                onClick={() => setCurrentPage(page - 1)}
              >
                {page}
              </button>
            )
          )}

          <button
            className="btn p-0 fs-5 pagination-item"
            style={{ width: "40px", height: "40px" }}
            onClick={() =>
              setCurrentPage((prev) =>
                Math.min(
                  Math.ceil(filteredResults.length / rowsPerPage) - 1,
                  prev + 1
                )
              )
            }
            disabled={
              currentPage >= Math.ceil(filteredResults.length / rowsPerPage) - 1
            }
          >
            &gt;
          </button>
        </div>
      </div>
    </div>
  );
};

export default IncidentMisReport;
